import * as React from 'react';
import { Image } from '@/hen-ui/atomics/Image';
import { Text } from '@/hen-ui/atomics/Text';
import { Link } from '@/hen-ui/atomics/Link';
import { Separator } from '@/hen-ui/atomics/Separator';
import { buttonVariants } from '@/hen-ui/atomics/Button/button.variants';
import { Flex } from '@/hen-ui/composites/Flex';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import { cn } from '@/hen-ui/utils';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';

export interface ArticleCardOverlayProps {
  image?: string;
  imageAlt?: string;
  title: string;
  brow?: string;
  browLabel?: string;
  author?: string;
  date?: string;
  href?: string;
}

/**
 * Full-bleed article tile: cover image, bottom gradient, left-aligned white type.
 * Used by {@link ArticleCard} when `layout="overlay"`. Props match the stacked card’s data model; only layout differs.
 */
export const ArticleCardOverlay = React.forwardRef<
  HTMLDivElement,
  ArticleCardOverlayProps
>(({ image, imageAlt, title, brow, browLabel, author, date, href }, ref) => {
  const resolvedBrow = browLabel ?? brow;

  const overlayBody = (
    <Flex
      ref={href ? undefined : ref}
      direction='column'
      gap='none'
      className={cn(
        'overflow-hidden relative gap-0 w-full h-full rounded-xl group min-h-article-card-overlay',
      )}
      role='article'
      aria-label={imageAlt ?? title}
      data-hen-id={COMPONENT_IDS.ArticleCard}
    >
      {image ? (
        <Image
          src={image}
          alt={imageAlt ?? title}
          loading='lazy'
          decoding='async'
          rounded={false}
          objectFit='cover'
          className={cn(
            'object-cover absolute inset-0 z-0 w-full h-full origin-center',
            'transition-transform ease-out duration-slow motion-reduce:transition-none',
            'md:group-hover:scale-hover-zoom',
          )}
        />
      ) : null}
      <Flex
        direction='column'
        gap='none'
        aria-hidden
        className='pointer-events-none absolute inset-0 z-[1] h-full w-full bg-image-card-overlay'
      />
      <Flex
        direction='column'
        align='start'
        gap='xxs'
        className='absolute inset-x-0 bottom-0 z-[2] w-full min-w-0 p-2xl text-left transition-transform duration-slow motion-reduce:transition-none md:group-hover:-translate-y-xl'
      >
        <SectionHeader
          size='h3'
          align='left'
          color='white'
          brow={resolvedBrow}
          title={title}
          titleClassName='line-clamp-2'
        />
        {(author || date) && (
          <Flex direction='row' gap='xxs' align='center' wrap='wrap'>
            {author && (
              <Text variant='l1' color='white'>
                By {author}
              </Text>
            )}
            {author && date && (
              <Separator
                orientation='vertical'
                className='h-3.5 w-px shrink-0 bg-primary-100'
              />
            )}
            {date && (
              <Text variant='l1' color='white'>
                {date}
              </Text>
            )}
          </Flex>
        )}
        {href && (
          <div className='hidden md:block overflow-hidden max-h-0 opacity-0 transition-all duration-slow motion-reduce:transition-none group-hover:max-h-3xl group-hover:opacity-100 group-hover:mt-xxs'>
            <span
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
              aria-hidden='true'
            >
              Read More
            </span>
          </div>
        )}
      </Flex>
    </Flex>
  );

  if (href) {
    return (
      <Flex ref={ref} direction='column' gap='none' className='w-full h-full'>
        <Link
          href={href}
          underline='none'
          aria-label={`Link to: ${title}`}
          className='w-full h-full'
        >
          {overlayBody}
        </Link>
      </Flex>
    );
  }

  return overlayBody;
});

ArticleCardOverlay.displayName = 'ArticleCardOverlay';
